import type {
  ISharedTranslations,
  IHolistoSpecificTranslations,
  IAlgotelsSpecificTranslations,
  ISplittySpecificTranslations,
  ITrivagodealsSpecificTranslations,
  IZaptravelSpecificTranslations,
  ITraveluroSpecificTranslations,
} from "@holisto-web/translations";
import i18next, { type i18n } from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import { getMockConfiguration } from "./globals-configuration-brand.js";
import { deTranslationsMock, getI18nMock as getStorybookI18nMock } from "@test-utils/translations";
import { translations as algotelsTranslations } from "@holisto-web/translations/brands/algotels";
import { translations as holistoTranslations } from "@holisto-web/translations/brands/holisto";
import { translations as splittyTranslations } from "@holisto-web/translations/brands/splitty";
import { translations as traveluroTranslations } from "@holisto-web/translations/brands/traveluro";
import { translations as trivagodealsTranslations } from "@holisto-web/translations/brands/trivagodeals";
import { translations as zaptravelTranslations } from "@holisto-web/translations/brands/zaptravel";

let instance: i18n;
const defaultLanguage = "EN";
const germanLanguage = "DE";

const translationsPerBrand: Record<
  BrandId,
  | typeof holistoTranslations.dictionary
  | typeof zaptravelTranslations.dictionary
  | typeof trivagodealsTranslations.dictionary
  | typeof splittyTranslations.dictionary
  | typeof algotelsTranslations.dictionary
  | typeof traveluroTranslations.dictionary
> = {
  holisto: holistoTranslations.dictionary,
  zaptravel: zaptravelTranslations.dictionary,
  trivagodeals: trivagodealsTranslations.dictionary,
  splitty: splittyTranslations.dictionary,
  algotels: algotelsTranslations.dictionary,
  traveluro: traveluroTranslations.dictionary,
};

const getCurrentTestLanguage = () => {
  const storybookI18n = getStorybookI18nMock();
  const language = storybookI18n.resolvedLanguage ?? storybookI18n.language ?? defaultLanguage;

  return language.toUpperCase();
};

export const getI18nMock = (brandId: BrandId) => {
  if (!instance) {
    const brandConfig = getMockConfiguration(brandId);

    const newInstance = i18next.createInstance({
      interpolation: {
        defaultVariables: {
          contactUsPhoneNumber: brandConfig.contactUsPhoneNumber,
          brand: brandConfig.brandName,
          hostName: brandConfig.hostName,
        },
      },
    });

    newInstance
      .use(initReactI18next)
      .use(I18nextBrowserLanguageDetector)
      .use(ICU)
      .init({
        fallbackLng: defaultLanguage,
        resources: {
          [defaultLanguage]: {
            [brandId]: translationsPerBrand[brandId],
          },
          [germanLanguage]: {
            [brandId]: deTranslationsMock,
          },
        },
      });

    instance = instance || newInstance;
  }

  return instance;
};

export function getTranslations(brandId: BrandId) {
  return {
    t: (
      key: PathsToFields<
        | ISharedTranslations
        | IAlgotelsSpecificTranslations
        | IHolistoSpecificTranslations
        | ISplittySpecificTranslations
        | ITraveluroSpecificTranslations
        | ITrivagodealsSpecificTranslations
        | IZaptravelSpecificTranslations
      >,
      dictionary?: Record<string, string | number>,
    ): string => {
      const i18n = getI18nMock(brandId);
      i18n.setDefaultNamespace(brandId);
      const language = getCurrentTestLanguage();
      const result = i18n.t(key, { ...dictionary, lng: language });
      if (key === result) {
        console.error("Translation key not found", brandId, JSON.stringify(i18n.store, null, 2));
        throw new Error("Translation key not found");
      }

      return result;
    },
  };
}
